import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Dialog, IconButton, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import { useTranslation } from "react-i18next";

const MIN_ZOOM = 1;
const MAX_ZOOM = 10;
const ZOOM_STEP = 0.35;
const MIN_W = 480;
const MIN_H = 360;

function clampZoom(value) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
}

function defaultSize() {
  if (typeof window === "undefined") {
    return { width: 900, height: 700 };
  }
  return {
    width: Math.min(1100, Math.max(MIN_W, window.innerWidth * 0.88)),
    height: Math.min(860, Math.max(MIN_H, window.innerHeight * 0.86)),
  };
}

/**
 * Visor grande redimensionable con zoom (imágenes y PDF).
 * Sustituye el Popover pequeño de FileThumbnails.
 */
export default function ZoomableFilePreviewDialog({
  open,
  onClose,
  title = "",
  kind = "image", // "image" | "pdf"
  src = null,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [size, setSize] = useState(defaultSize);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const stageRef = useRef(null);
  const viewRef = useRef({ zoom: 1, offset: { x: 0, y: 0 } });
  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const resizeRef = useRef(null);
  const isZoomed = zoom > 1.01;

  viewRef.current = { zoom, offset };

  const resetView = useCallback(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setDragging(false);
    dragRef.current = null;
    pinchRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) return;
    setSize(defaultSize());
    resetView();
  }, [open, src, resetView]);

  const applyZoomAtPoint = useCallback((nextZoom, clientX, clientY) => {
    const stage = stageRef.current;
    const { zoom: prevZoom, offset: prevOffset } = viewRef.current;
    const zoomed = clampZoom(nextZoom);
    if (zoomed <= MIN_ZOOM) {
      setZoom(MIN_ZOOM);
      setOffset({ x: 0, y: 0 });
      return;
    }
    if (!stage) {
      setZoom(zoomed);
      return;
    }
    const rect = stage.getBoundingClientRect();
    const pointX = clientX - rect.left - rect.width / 2;
    const pointY = clientY - rect.top - rect.height / 2;
    const ratio = zoomed / prevZoom;
    setZoom(zoomed);
    setOffset({
      x: pointX - (pointX - prevOffset.x) * ratio,
      y: pointY - (pointY - prevOffset.y) * ratio,
    });
  }, []);

  const bumpZoom = useCallback(
    (delta) => {
      const stage = stageRef.current;
      const { zoom: current } = viewRef.current;
      if (!stage) {
        const next = clampZoom(current + delta);
        setZoom(next);
        if (next <= MIN_ZOOM) setOffset({ x: 0, y: 0 });
        return;
      }
      const rect = stage.getBoundingClientRect();
      applyZoomAtPoint(
        current + delta,
        rect.left + rect.width / 2,
        rect.top + rect.height / 2,
      );
    },
    [applyZoomAtPoint],
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      const tag = String(event.target?.tagName || "").toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        event.target?.isContentEditable
      ) {
        return;
      }
      if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        bumpZoom(ZOOM_STEP);
      } else if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        bumpZoom(-ZOOM_STEP);
      } else if (event.key === "0") {
        event.preventDefault();
        resetView();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, bumpZoom, resetView]);

  useEffect(() => {
    if (!open || !src) return undefined;

    const onWheel = (event) => {
      const stage = stageRef.current;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      const overStage =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!overStage) return;

      // PDF multipágina: rueda = scroll de páginas; Ctrl/Cmd+rueda = zoom.
      // Imagen: rueda = zoom directo.
      const wantsZoom = kind === "image" || event.ctrlKey || event.metaKey;
      if (!wantsZoom) return;

      event.preventDefault();
      event.stopPropagation();
      const direction = event.deltaY < 0 ? 1 : -1;
      applyZoomAtPoint(
        viewRef.current.zoom + direction * ZOOM_STEP,
        event.clientX,
        event.clientY,
      );
    };

    // En captura para que Ctrl+rueda funcione también sobre el iframe del PDF.
    window.addEventListener("wheel", onWheel, {
      passive: false,
      capture: true,
    });
    return () =>
      window.removeEventListener("wheel", onWheel, { capture: true });
  }, [open, src, kind, applyZoomAtPoint]);

  const handleDoubleClick = (event) => {
    if (!src || kind === "pdf") return;
    event.preventDefault();
    if (isZoomed) {
      resetView();
      return;
    }
    applyZoomAtPoint(2.5, event.clientX, event.clientY);
  };

  const handlePointerDown = (event) => {
    // En PDF sin zoom dejamos que el iframe reciba el scroll/clic.
    if (!src || zoom <= 1.01 || event.button !== 0) return;
    if (kind === "pdf" && event.target?.tagName === "IFRAME") return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: offset.x,
      originY: offset.y,
    };
    setDragging(true);
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setOffset({
      x: drag.originX + (event.clientX - drag.startX),
      y: drag.originY + (event.clientY - drag.startY),
    });
  };

  const handlePointerUp = (event) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
      setDragging(false);
    }
  };

  const touchDistance = (touches) => {
    const [a, b] = touches;
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  };

  const touchCenter = (touches) => {
    const [a, b] = touches;
    return {
      x: (a.clientX + b.clientX) / 2,
      y: (a.clientY + b.clientY) / 2,
    };
  };

  const handleTouchStart = (event) => {
    if (!src || event.touches.length !== 2) return;
    event.preventDefault();
    dragRef.current = null;
    setDragging(false);
    const center = touchCenter(event.touches);
    pinchRef.current = {
      startDist: touchDistance(event.touches),
      startZoom: viewRef.current.zoom,
      centerX: center.x,
      centerY: center.y,
    };
  };

  const handleTouchMove = (event) => {
    const pinch = pinchRef.current;
    if (!pinch || event.touches.length !== 2) return;
    event.preventDefault();
    const dist = touchDistance(event.touches);
    if (!pinch.startDist) return;
    applyZoomAtPoint(
      pinch.startZoom * (dist / pinch.startDist),
      pinch.centerX,
      pinch.centerY,
    );
  };

  const handleTouchEnd = () => {
    pinchRef.current = null;
  };

  const startResize = (event) => {
    event.preventDefault();
    event.stopPropagation();
    resizeRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startW: size.width,
      startH: size.height,
    };
    const onMove = (ev) => {
      const r = resizeRef.current;
      if (!r) return;
      const maxW = window.innerWidth - 24;
      const maxH = window.innerHeight - 24;
      setSize({
        width: Math.min(
          maxW,
          Math.max(MIN_W, r.startW + (ev.clientX - r.startX)),
        ),
        height: Math.min(
          maxH,
          Math.max(MIN_H, r.startH + (ev.clientY - r.startY)),
        ),
      });
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const maximize = () => {
    setSize({
      width: Math.max(MIN_W, window.innerWidth - 24),
      height: Math.max(MIN_H, window.innerHeight - 24),
    });
  };

  const zoomPercent = Math.round(zoom * 100);
  const fileLabel =
    title ||
    (kind === "pdf"
      ? t("Samples.filePreviewPdf", { defaultValue: "PDF" })
      : t("Samples.filePreviewImage", { defaultValue: "Imagen" }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      PaperProps={{
        sx: {
          width: size.width,
          height: size.height,
          maxWidth: "calc(100vw - 16px)",
          maxHeight: "calc(100vh - 16px)",
          m: 1,
          bgcolor: "rgba(12,12,12,0.97)",
          backgroundImage: "none",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 1.5,
          py: 1,
          color: "common.white",
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle2"
          noWrap
          title={fileLabel}
          sx={{ fontWeight: 700, minWidth: 0 }}
        >
          {fileLabel}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Tooltip title={t("Samples.photoViewerZoomOut")}>
            <span>
              <IconButton
                size="small"
                onClick={() => bumpZoom(-ZOOM_STEP)}
                disabled={zoom <= MIN_ZOOM}
                sx={{ color: "common.white" }}
              >
                <ZoomOutOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{
              minWidth: 44,
              textAlign: "center",
              fontWeight: 800,
              opacity: 0.9,
            }}
          >
            {zoomPercent}%
          </Typography>
          <Tooltip title={t("Samples.photoViewerZoomIn")}>
            <span>
              <IconButton
                size="small"
                onClick={() => bumpZoom(ZOOM_STEP)}
                disabled={zoom >= MAX_ZOOM}
                sx={{ color: "common.white" }}
              >
                <ZoomInOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("Samples.photoViewerZoomReset")}>
            <span>
              <IconButton
                size="small"
                onClick={resetView}
                disabled={!isZoomed}
                sx={{ color: "common.white" }}
              >
                <RestartAltOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={t("Samples.filePreviewMaximize", {
              defaultValue: "Maximizar",
            })}
          >
            <IconButton
              size="small"
              onClick={maximize}
              sx={{ color: "common.white" }}
            >
              <OpenInFullOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton
            aria-label={t("common.close", { defaultValue: "Cerrar" })}
            onClick={onClose}
            sx={{ color: "common.white" }}
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Box>
      </Box>

      <Box
        ref={stageRef}
        onDoubleClick={handleDoubleClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          mx: 1.5,
          mb: 1.5,
          borderRadius: 1.5,
          bgcolor: alpha(theme.palette.common.white, 0.06),
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          touchAction: kind === "pdf" && !isZoomed ? "auto" : "none",
          cursor:
            kind === "pdf" && !isZoomed
              ? "default"
              : isZoomed
                ? dragging
                  ? "grabbing"
                  : "grab"
                : "zoom-in",
          userSelect: "none",
        }}
      >
        {src && kind === "image" ? (
          <Box
            component="img"
            src={src}
            alt={fileLabel}
            draggable={false}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              display: "block",
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
              transformOrigin: "center center",
              transition: dragging ? "none" : "transform 80ms ease-out",
              willChange: "transform",
              pointerEvents: "none",
            }}
          />
        ) : null}

        {src && kind === "pdf" ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
              transformOrigin: "center center",
              transition: dragging ? "none" : "transform 80ms ease-out",
              willChange: "transform",
              // Sin zoom: el PDF recibe la rueda para pasar páginas.
              // Con zoom: bloqueamos el iframe y se hace pan arrastrando.
              pointerEvents: isZoomed ? "none" : "auto",
            }}
          >
            <Box
              component="iframe"
              src={src}
              title={fileLabel}
              sx={{
                width: "100%",
                height: "100%",
                border: "none",
                display: "block",
                bgcolor: "common.white",
              }}
            />
          </Box>
        ) : null}

        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            left: 12,
            bottom: 10,
            px: 1,
            py: 0.35,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.common.black, 0.45),
            color: "common.white",
            fontWeight: 600,
            pointerEvents: "none",
          }}
        >
          {kind === "pdf"
            ? t("Samples.filePreviewPdfZoomHint")
            : t("Samples.photoViewerZoomHint")}
        </Typography>
      </Box>

      <Box
        onPointerDown={startResize}
        title={t("Samples.filePreviewResize", {
          defaultValue: "Arrastra para redimensionar",
        })}
        sx={{
          position: "absolute",
          right: 4,
          bottom: 4,
          width: 22,
          height: 22,
          cursor: "nwse-resize",
          borderRadius: "4px 0 8px 0",
          bgcolor: alpha(theme.palette.common.white, 0.18),
          border: "1px solid",
          borderColor: alpha(theme.palette.common.white, 0.35),
          zIndex: 3,
          "&::after": {
            content: '""',
            position: "absolute",
            right: 4,
            bottom: 4,
            width: 10,
            height: 10,
            borderRight: "2px solid",
            borderBottom: "2px solid",
            borderColor: alpha(theme.palette.common.white, 0.85),
          },
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.45),
          },
        }}
      />
    </Dialog>
  );
}
